import { FormControl } from '@mui/material';
import FilterAutocompleteSelection from 'Components/Filter/FilterAutocompleteSelection';
import usePartners from 'FeatureModules/Partner/Components/hooks/usePartners';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { Skeleton } from 'DesignSystem';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { ECompanyStatus } from 'FeatureModules/Auth/types/company.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';

interface IPartnersAutocompleteProps {
  inviteNewPartner?: boolean;
  defaultValue?: string[];
  placeholder?: string;
  /* Default is true */
  filterByRegisteredPartners?: boolean;
  excludeOwnCompany?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  onPartnerSelectionChange: (newSelection: IBasePartnerCompany[]) => void;
  'data-testid'?: string;
}

const PartnersAutocomplete: FC<IPartnersAutocompleteProps> = ({
  defaultValue = [],
  size = 'medium',
  filterByRegisteredPartners = true,
  excludeOwnCompany,
  disabled,
  hasError,
  errorMessage = 'Please select an owner',
  onPartnerSelectionChange,
}) => {
  const [selectPartnerId, setSelectPartnerId] = useState<string | null>(null);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState<string[]>(defaultValue);

  const { company } = useCompanyPlanContext();
  const { partners, loading } = usePartners({ skip: disabled });

  const partnerOptions = useMemo(() => {
    if (filterByRegisteredPartners) {
      return [
        ...partners.filter(partner => partner.status === ECompanyStatus.REGISTERED),
        ...(excludeOwnCompany ? [] : [company]),
      ].map(partner => ({
        id: partner.id,
        title: partner.name,
      }));
    }
    return [...partners, ...(excludeOwnCompany ? [] : [company])].map(partner => ({
      id: partner.id,
      title: partner.name,
    }));
  }, [partners, company, filterByRegisteredPartners, excludeOwnCompany]);

  const handleSelectPartnerSelectionChange = (newValues?: string[] | string) => {
    setSelectedPartnerIds(newValues as string[]);
    onPartnerSelectionChange(
      newValues ? partners.filter(partner => newValues.includes(partner.id)) : []
    );
  };

  /**
   * Waiting until the partner list got updated and select the correct partner as soon as it exists
   * Removing the selectedPartner from the temporary state variable after the partner got created
   */
  useEffect(() => {
    if (selectPartnerId) {
      const partner = partners.find(({ id }) => id === selectPartnerId);
      if (!partner) {
        // eslint-disable-next-line no-console
        console.error(`Partner with id ${selectPartnerId} not found`);
        setSelectPartnerId(null);
        return;
      }
      setSelectedPartnerIds(prev => [...prev, partner.id]);
      onPartnerSelectionChange([
        ...partners.filter(p => selectedPartnerIds.includes(p.id)),
        partner,
      ]);
      setSelectPartnerId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectPartnerId, partners]);

  return (
    <FormControl fullWidth size={size} error={hasError}>
      {loading ? (
        <Skeleton variant="rounded" width="100%" height={30} />
      ) : (
        <FilterAutocompleteSelection
          multiple
          size={size}
          placeholder="Select partners"
          closeAfterSelection
          enableAddNewItem
          itemName="partner"
          items={partnerOptions}
          errorMessage={errorMessage}
          hasError={hasError}
          selected={selectedPartnerIds}
          onSelectionChange={handleSelectPartnerSelectionChange}
        />
      )}
    </FormControl>
  );
};

export default PartnersAutocomplete;
